import { useMemo, useState } from "react";
import { Section } from "views/applications-id-report/components/Section";
import { toMoney } from "shared/utils";
import { DateTime } from "luxon";
import { Hide, Show } from "shared/components";
import { NetIncomeChart } from "./NetIncomeGraphSlice";
import { ApplicationStatus, VerificationStatus } from "shared/types";
import { RerunGlobalWarning } from "../components/RerunGlobalWarning";
import { mapNetIncomeAnalysis } from "../helpers";
import { ExpandableTable } from "../components/UniversalTable";
import { AdjustedApplicationRentActivityLog } from "views/applications-id-report/components/AdjustedApplicationRentIncomeSection";
import { IncomeSlice, type IncomeDetailTab } from "./TablesSlice/IncomeSlice";
import { DocumentWarnings } from "./DocumentWarningsSlice";
import { PrintReady } from "views/applications-id-report/providers/PrintProvider";
import { getAdditionalDataPerVerificationType } from "../utils";
import { NoIncomeSection } from "../components/NoIncomeSection";
import { NetIncomeAnalysisExpandedRow } from "../components/NetIncomeAnalysisExpandedRow";
import { IncomeSummary } from "../components/IncomeSummary";
import { DebtDetails } from "../components/DebtDetails";
import {
  COMPLETE_VERIFICATION_STATUSES,
  INCOMPLETE_APPLICATION_STATUSES,
  INCOMPLETE_VERIFICATION_STATUSES
} from "../constants";
import {
  getIncomeReverifyRequest,
  getLatestReverifyRequestForIncome
} from "./ReverifySlice/utils";
import { useCombinedIncomeContext } from "../providers/useCombinedIncome";
import { ReportFallbackSlice } from "./ReportFallbackSlice";
import { AdditionalQuestionsReportSection } from "views/applications-id-report/components/AdditionalQuestionsReportSection";

export const ReportContentSlice = () => (
  <ReportFallbackSlice>
    <ReportBody />
  </ReportFallbackSlice>
);

const ReportBody = () => {
  // BSCPF-6584. Owned here rather than in IncomeSlice because the report-level sections
  // after the incomes loop are hidden while an employer verification survey is open.
  const [detailTabs, setDetailTabs] = useState<Record<number, IncomeDetailTab>>(
    {}
  );

  const {
    applicationMeta,
    currentlySelectedVerificationId,
    verificationData: reportData,
    verificationReportData: data,
    incomes,
    bankStatements,
    pendingReverifyRequests,
    completedReverifyRequests,
    failedReverifyRequests,
    verificationStatus,
    applicationRentActivityLogsMeta,
    hideProjectedDebtSection
  } = useCombinedIncomeContext();

  // Keyed by income id and never pruned, so it is read through the incomes actually on
  // screen *and* their employer verification — the same gate IncomeSlice renders the tab
  // behind. A rerun replaces the income list, and a refetch can drop the verification
  // while keeping the income; either way a remembered "employer" tab would otherwise keep
  // the debt and rent-activity sections hidden for good.
  const isAnyEmployerTabActive = useMemo(
    () =>
      incomes.some(
        (income) =>
          Boolean(income.employer_verification) &&
          detailTabs[income.id] === "employer"
      ),
    [incomes, detailTabs]
  );

  if (!data || !reportData || !applicationMeta.data) return null;

  const noIncome = data.incomes.find((income) => income.type === "no_income");
  const summary = data.total_summary;
  const netIncomeData = data.net_income_analysis;
  const additionalQuestions = (
    <AdditionalQuestionsReportSection
      answers={data.additional_questions}
      questions={reportData.settings?.additional_questions}
    />
  );

  const isVerificationIncomplete =
    INCOMPLETE_VERIFICATION_STATUSES.includes(verificationStatus) &&
    INCOMPLETE_APPLICATION_STATUSES.includes(
      applicationMeta.data.status as ApplicationStatus
    );

  const isRerun = Boolean(reportData.rerun_version);
  const isPostCreatedRequest =
    Boolean(reportData.is_post_created) &&
    reportData.status !== VerificationStatus.Canceled &&
    !reportData.archived_at;
  const shouldShowGlobalWarning = isRerun || isPostCreatedRequest;
  const initiatedBy = reportData.requested_by?.name;
  const initiatedAt = reportData.requested_at ?? reportData.created_at ?? null;
  const globalWarning = shouldShowGlobalWarning ? (
    <RerunGlobalWarning
      initiatedBy={initiatedBy}
      initiatedAt={initiatedAt}
      isFinished={COMPLETE_VERIFICATION_STATUSES.includes(verificationStatus)}
    />
  ) : null;

  if (noIncome) {
    const housingAssistance = data.incomes.find(
      (income) => income.type === "housing_assistance"
    );

    return (
      <>
        {globalWarning}
        <NoIncomeSection
          noIncome={noIncome}
          housingAssistance={housingAssistance}
          additionalQuestions={additionalQuestions}
        />
      </>
    );
  }

  return (
    <div>
      {globalWarning}
      <Show if={currentlySelectedVerificationId}>
        <PrintReady verificationId={currentlySelectedVerificationId!} />
      </Show>

      <Section
        sectionTitle="Income summary"
        maxWidth="100%"
        sectionClassName="p-0"
      >
        <IncomeSummary />
      </Section>

      {additionalQuestions}

      <DocumentWarnings />

      <Hide if={isVerificationIncomplete}>
        <Section sectionTitle="Net income analysis" maxWidth="1100px">
          <NetIncomeChart data={netIncomeData} />

          <div className="mt-[60px]">
            <ExpandableTable
              hideHeader
              rowClassName="items-center pr-4"
              headerRowClassName="text-content-1000 text-sm font-medium"
              columns={[
                {
                  header: "month",
                  accessor: (row) =>
                    DateTime.fromISO(row.month).toFormat("MMMM, yyyy"),
                  widthClass: "w-full"
                },
                {
                  header: "total",
                  accessor: (row) => toMoney(row.total),
                  widthClass: "w-[120px] shrink-0"
                }
              ]}
              rows={mapNetIncomeAnalysis(netIncomeData)}
              renderExpanded={(original) => (
                <NetIncomeAnalysisExpandedRow original={original} />
              )}
            />
          </div>
        </Section>
      </Hide>

      {incomes.map((income, index) => (
        <IncomeSlice
          key={index}
          income={income}
          activeDetailTab={detailTabs[income.id] ?? "self_reported"}
          onDetailTabChange={(tab) =>
            setDetailTabs((prev) => ({ ...prev, [income.id]: tab }))
          }
          additionalData={getAdditionalDataPerVerificationType(
            data,
            income,
            bankStatements
          )}
          pendingReverifyRequest={getIncomeReverifyRequest(
            income.id,
            pendingReverifyRequests
          )}
          completedReverifyRequest={getLatestReverifyRequestForIncome(
            income.id,
            completedReverifyRequests
          )}
          failedReverifyRequest={getLatestReverifyRequestForIncome(
            income.id,
            failedReverifyRequests
          )}
        />
      ))}

      <Hide
        if={
          isAnyEmployerTabActive ||
          hideProjectedDebtSection ||
          Boolean(
            !summary.debt_gross_income_ratio &&
            !summary.debt_net_income_ratio &&
            !summary.debt_self_attested_gross_income_ratio
          )
        }
      >
        <DebtDetails
          summary={summary}
          isVerificationIncomplete={isVerificationIncomplete}
        />
      </Hide>

      <Hide if={isAnyEmployerTabActive}>
        <div className="space-y-8 p-6 pt-0 text-sm">
          {applicationRentActivityLogsMeta.data?.map((activityItem, index) => (
            <AdjustedApplicationRentActivityLog
              key={index}
              activityItem={activityItem}
            />
          ))}
        </div>
      </Hide>
    </div>
  );
};
